import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Table, Input, Button, Space, Popconfirm, Typography } from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
} from '@ant-design/icons';
import type { TaskResultSqlOutput } from './evalTypes';

const { Text } = Typography;

interface EditableTableProps {
    value: TaskResultSqlOutput;
    onChange: (val: TaskResultSqlOutput) => void;
}

// --- Editable Cell ---

interface EditableCellProps {
    value: unknown;
    onSave: (val: unknown) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({ value, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const inputRef = useRef<any>(null);

    useEffect(() => {
        if (editing) inputRef.current?.focus();
    }, [editing]);

    const startEdit = () => {
        setDraft(value === null || value === undefined ? '' : String(value));
        setEditing(true);
    };

    const save = () => {
        setEditing(false);
        const trimmed = draft.trim();
        if (trimmed === '') { onSave(null); return; }
        const num = Number(trimmed);
        if (!Number.isNaN(num) && trimmed !== '') { onSave(num); return; }
        if (trimmed.toLowerCase() === 'true') { onSave(true); return; }
        if (trimmed.toLowerCase() === 'false') { onSave(false); return; }
        onSave(trimmed);
    };

    if (editing) {
        return (
            <Input
                ref={inputRef}
                size="small"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPressEnter={save}
                onBlur={save}
                style={{ minWidth: 60 }}
            />
        );
    }

    const display = value === null || value === undefined
        ? <Text type="secondary" italic>null</Text>
        : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);

    return (
        <div
            onClick={startEdit}
            style={{ cursor: 'pointer', minHeight: 22, padding: '0 4px' }}
            title="Click to edit"
        >
            {display}
        </div>
    );
};

// --- Editable Column Header ---

interface EditableHeaderProps {
    name: string;
    onRename: (newName: string) => void;
    onDelete: () => void;
    onSort: (dir: 'asc' | 'desc') => void;
    onMoveLeft?: () => void;
    onMoveRight?: () => void;
}

const EditableHeader: React.FC<EditableHeaderProps> = ({ name, onRename, onDelete, onSort, onMoveLeft, onMoveRight }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const inputRef = useRef<any>(null);

    useEffect(() => {
        if (editing) inputRef.current?.focus();
    }, [editing]);

    const save = () => {
        setEditing(false);
        const trimmed = draft.trim();
        if (trimmed && trimmed !== name) onRename(trimmed);
    };

    if (editing) {
        return (
            <Input
                ref={inputRef}
                size="small"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPressEnter={save}
                onBlur={save}
                style={{ width: 100 }}
            />
        );
    }

    return (
        <Space size={2}>
            {onMoveLeft && (
                <ArrowUpOutlined
                    style={{ fontSize: 10, cursor: 'pointer', transform: 'rotate(-90deg)' }}
                    onClick={(e) => { e.stopPropagation(); onMoveLeft(); }}
                    title="Move left"
                />
            )}
            <span
                onClick={() => { setDraft(name); setEditing(true); }}
                style={{ cursor: 'pointer' }}
                title="Click to rename"
            >
                {name}
            </span>
            <SortAscendingOutlined
                style={{ fontSize: 11, cursor: 'pointer', opacity: 0.5 }}
                onClick={(e) => { e.stopPropagation(); onSort('asc'); }}
                title="Sort ascending"
            />
            <SortDescendingOutlined
                style={{ fontSize: 11, cursor: 'pointer', opacity: 0.5 }}
                onClick={(e) => { e.stopPropagation(); onSort('desc'); }}
                title="Sort descending"
            />
            {onMoveRight && (
                <ArrowDownOutlined
                    style={{ fontSize: 10, cursor: 'pointer', transform: 'rotate(-90deg)' }}
                    onClick={(e) => { e.stopPropagation(); onMoveRight(); }}
                    title="Move right"
                />
            )}
            <Popconfirm
                title="Delete this column?"
                onConfirm={onDelete}
                okText="Yes"
                cancelText="No"
            >
                <DeleteOutlined
                    style={{ fontSize: 11, cursor: 'pointer', color: '#ff4d4f', opacity: 0.6 }}
                    onClick={(e) => e.stopPropagation()}
                    title="Delete column"
                />
            </Popconfirm>
        </Space>
    );
};

// --- Main EditableTable ---

export const EditableTable: React.FC<EditableTableProps> = ({ value, onChange }) => {
    const { columns, rows } = value;

    const updateCell = useCallback((rowIdx: number, col: string, cellVal: unknown) => {
        const newRows = rows.map((r, i) =>
            i === rowIdx ? { ...r, [col]: cellVal } : r,
        );
        onChange({ columns, rows: newRows });
    }, [columns, rows, onChange]);

    const renameColumn = useCallback((oldName: string, newName: string) => {
        if (columns.includes(newName)) return;
        const newCols = columns.map((c) => (c === oldName ? newName : c));
        const newRows = rows.map((r) => {
            const nr: Record<string, unknown> = {};
            for (const c of columns) {
                nr[c === oldName ? newName : c] = r[c];
            }
            return nr;
        });
        onChange({ columns: newCols, rows: newRows });
    }, [columns, rows, onChange]);

    const deleteColumn = useCallback((col: string) => {
        const newCols = columns.filter((c) => c !== col);
        const newRows = rows.map((r) => {
            const nr: Record<string, unknown> = {};
            for (const c of newCols) nr[c] = r[c];
            return nr;
        });
        onChange({ columns: newCols, rows: newRows });
    }, [columns, rows, onChange]);

    const addColumn = useCallback(() => {
        let name = `col_${columns.length + 1}`;
        let n = columns.length + 1;
        while (columns.includes(name)) { n++; name = `col_${n}`; }
        const newCols = [...columns, name];
        const newRows = rows.map((r) => ({ ...r, [name]: null }));
        onChange({ columns: newCols, rows: newRows });
    }, [columns, rows, onChange]);

    const sortByColumn = useCallback((col: string, dir: 'asc' | 'desc') => {
        const sorted = [...rows].sort((a, b) => {
            const va = a[col];
            const vb = b[col];
            if (va === vb) return 0;
            if (va === null || va === undefined) return 1;
            if (vb === null || vb === undefined) return -1;
            if (typeof va === 'number' && typeof vb === 'number') return dir === 'asc' ? va - vb : vb - va;
            const sa = String(va), sb = String(vb);
            return dir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
        });
        onChange({ columns, rows: sorted });
    }, [columns, rows, onChange]);

    const moveColumn = useCallback((colIdx: number, direction: -1 | 1) => {
        const target = colIdx + direction;
        if (target < 0 || target >= columns.length) return;
        const newCols = [...columns];
        [newCols[colIdx], newCols[target]] = [newCols[target], newCols[colIdx]];
        onChange({ columns: newCols, rows });
    }, [columns, rows, onChange]);

    const addRow = useCallback(() => {
        const newRow: Record<string, unknown> = {};
        columns.forEach((c) => { newRow[c] = null; });
        onChange({ columns, rows: [...rows, newRow] });
    }, [columns, rows, onChange]);

    const deleteRow = useCallback((rowIdx: number) => {
        onChange({ columns, rows: rows.filter((_, i) => i !== rowIdx) });
    }, [columns, rows, onChange]);

    const moveRow = useCallback((rowIdx: number, direction: -1 | 1) => {
        const target = rowIdx + direction;
        if (target < 0 || target >= rows.length) return;
        const newRows = [...rows];
        [newRows[rowIdx], newRows[target]] = [newRows[target], newRows[rowIdx]];
        onChange({ columns, rows: newRows });
    }, [columns, rows, onChange]);

    if (columns.length === 0 && rows.length === 0) {
        return (
            <Space direction="vertical" size={4}>
                <Text type="secondary">No table data yet</Text>
                <Button size="small" icon={<PlusOutlined />} onClick={addColumn}>Add column</Button>
            </Space>
        );
    }

    const antColumns = [
        {
            title: '#',
            key: '_row_actions',
            width: 72,
            render: (_: unknown, __: unknown, rowIdx: number) => (
                <Space size={2}>
                    <ArrowUpOutlined
                        style={{ fontSize: 11, cursor: rowIdx > 0 ? 'pointer' : 'not-allowed', opacity: rowIdx > 0 ? 0.6 : 0.2 }}
                        onClick={() => rowIdx > 0 && moveRow(rowIdx, -1)}
                        title="Move up"
                    />
                    <ArrowDownOutlined
                        style={{ fontSize: 11, cursor: rowIdx < rows.length - 1 ? 'pointer' : 'not-allowed', opacity: rowIdx < rows.length - 1 ? 0.6 : 0.2 }}
                        onClick={() => rowIdx < rows.length - 1 && moveRow(rowIdx, 1)}
                        title="Move down"
                    />
                    <Popconfirm title="Delete row?" onConfirm={() => deleteRow(rowIdx)} okText="Yes" cancelText="No">
                        <DeleteOutlined style={{ fontSize: 11, cursor: 'pointer', color: '#ff4d4f', opacity: 0.6 }} title="Delete row" />
                    </Popconfirm>
                </Space>
            ),
        },
        ...columns.map((col, colIdx) => ({
            title: (
                <EditableHeader
                    name={col}
                    onRename={(n) => renameColumn(col, n)}
                    onDelete={() => deleteColumn(col)}
                    onSort={(dir) => sortByColumn(col, dir)}
                    onMoveLeft={colIdx > 0 ? () => moveColumn(colIdx, -1) : undefined}
                    onMoveRight={colIdx < columns.length - 1 ? () => moveColumn(colIdx, 1) : undefined}
                />
            ),
            dataIndex: col,
            key: col,
            render: (cellVal: unknown, _record: unknown, rowIdx: number) => (
                <EditableCell
                    value={cellVal}
                    onSave={(v) => updateCell(rowIdx, col, v)}
                />
            ),
        })),
    ];

    return (
        <div>
            <Table
                size="small"
                bordered
                pagination={false}
                scroll={{ x: true }}
                dataSource={rows.map((r, i) => ({ ...r, _key: i }))}
                rowKey="_key"
                columns={antColumns as any}
            />
            <Space style={{ marginTop: 6 }}>
                <Button size="small" icon={<PlusOutlined />} onClick={addRow}>Add row</Button>
                <Button size="small" icon={<PlusOutlined />} onClick={addColumn}>Add column</Button>
            </Space>
        </div>
    );
};

export default EditableTable;
